// The full build: the session recorder is bundled (ENGG-10416), so nothing is
// fetched from Mixpanel's CDN at run time.
import mixpanel from 'mixpanel-browser'

import { isRecordingAllowed, PUBLIC_ROUTE } from '../routes.constants'

/**
 * ENGG-10414 — Mixpanel product analytics for the admin's DSR surfaces.
 *
 * Hand-picked events only: no autocapture. Without REACT_APP_MIXPANEL_TOKEN the
 * whole module is a no-op, so dev, test and feature envs send nothing. Nothing
 * is sent on the public survey / sign-up pages (isRecordingAllowed, checked on
 * every call) or while an admin impersonates someone (their activity is not the
 * impersonated person's).
 *
 * ENGG-10416 — Session Replay runs only while a real admin is identified on a
 * page that may be recorded: it starts after the identify and stops on reset
 * (sign-out), when a session turns out to be an impersonation, on the public
 * pages and on the token sign-in route (`/TOKEN_LOGIN`, whose URL carries the
 * token: a replay batch carries the URL it started on, query included, outside
 * `property_blacklist`). Text is recorded as is, every input is masked, images
 * and videos are blocked (the SDK's default), console output is not recorded.
 * REACT_APP_MIXPANEL_RECORD_SESSIONS_PERCENT is the share of page loads that
 * start a recording (0–100, unset = 100, 0 = off). The SDK resumes a recording
 * of the same tab across a page load on its own as soon as it is initialised,
 * so it is initialised only once a real admin is identified on a page that may
 * be recorded (events from before are held, see below); a resumed recording
 * the page load does not want is stopped as soon as it is seen. A start lands
 * after its promise resolves, and a stop issued before it lands does not
 * cancel it, so every transition goes through one queue that watches the
 * recorder's actual state and stops each start that lands while recording is
 * not wanted.
 *
 * No URL goes to Mixpanel with an event: `$current_url` / `$referrer` /
 * `$initial_referrer` carry the full address, query and hash included, so they
 * are blacklisted, and every event carries `current_path` (the pathname alone)
 * instead.
 *
 * Every call is fire-and-forget and never throws: analytics must not be able to
 * break a save, a render or a logout.
 */

const DEFAULT_API_HOST = 'https://api-js.mixpanel.com'

// Events tracked after init but before the signed-in user is known (the user
// arrives with the page's own query, a route can mount in the same commit)
// wait here and go out once the user is identified. They are dropped when the
// user turns out to be impersonated, and on reset. Past the cap an event is
// dropped without a trace (nothing is logged): the queue only bridges the
// moments before the identity arrives, so a long wait is not worth buffering.
const MAX_WAITING_EVENTS = 20

// Set on every event by the SDK, with the full address (query and hash too).
const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_referrer']

// The person's profile fields, as super properties and on the people profile.
const PROFILE_KEYS = [
  'client_id',
  'client_name',
  'role',
  'is_tangelo_staff',
] as const

// ENGG-10416 — how long the module waits for a recorder start to land (its
// own, or the SDK's resume of this tab's previous recording after init or
// reset: async IndexedDB reads), polling for it meanwhile.
const SETTLE_MS = 3_000
const POLL_MS = 50
// Passes of one reconcile when the wanted state changes while it waits.
const MAX_ROUNDS = 3
// Starts that never landed (an opted-out or unsupported browser) before the
// module stops asking.
const MAX_FAILED_STARTS = 2
// After init or reset: when the module looks again for an SDK resume that
// landed late.
const LATE_CHECKS_MS = [2 * SETTLE_MS, 10 * SETTLE_MS]
// How often an admin identified on a page the SDK may not start on is tried again.
const DEFERRED_RETRY_MS = 1_000

type Primitive = string | number | boolean
export type EventProps = Record<string, Primitive | null | undefined>

export type AnalyticsUser = {
  id: number | string
  clientId?: number | string | null
  clientName?: string | null
  /** The role's type (SUPER_ADMIN, ...), never a customer-named role. */
  role?: string | null
  /** A Tangelo superadmin session (GET_CURRENT_USER dsrSuperAdminSession). */
  isTangeloStaff?: boolean
  impersonating?: boolean
}

// REACT_APP_MIXPANEL_TOKEN, once initAnalytics found one on an allowed page.
let token: string | null = null
// The SDK is initialised: lazily, at the first identify of a real admin on a
// page that may be recorded (ENGG-10416).
let initialised = false
// The distinct id events are sent as; null until a non-impersonated user is
// identified, and again after reset or when the session is an impersonation.
let identifiedAs: string | null = null
let identifiedSignature = ''
// True while this session must send nothing: after reset (signed out) and for
// an impersonated session. Cleared by the next successful identify.
let suspended = false
let waiting: Array<{ event: string; props: EventProps }> = []
// A real admin identified on a page the SDK could not start on (the token
// sign-in route); identified for real from the next call on another page.
let deferredUser: AnalyticsUser | null = null
// A retry of the deferred identify is scheduled.
let deferredRetry: number | null = null
// ENGG-10416 — until when a recorder start may still land: the latest the
// module asked for one, or the SDK may resume a recording by itself (after
// init and reset).
let startMayLandUntil = 0
let failedStarts = 0
// Recorder transitions, one at a time.
let transitions: Promise<void> = Promise.resolve()
// A reconcile is queued and has not begun; it reads the state when it does.
let reconcileQueued = false
// This page load's roll against the percent, made once.
let sampled: boolean | null = null

const environment = () => process.env.REACT_APP_ENVIRONMENT || 'development'

/**
 * The share of page loads recorded: the digits of an integer 0–100. Anything
 * else — unset, blank, "all", "12.5", "1e1", 101 — means every page load, so a
 * misspelt setting can never switch the replay off unnoticed.
 */
export const parseRecordSessionsPercent = (raw: unknown): number => {
  const text = String(raw ?? '').trim()
  const percent = /^\d{1,3}$/.test(text) ? Number(text) : NaN
  return percent <= 100 ? percent : 100
}

const recordSessionsPercent = (): number =>
  parseRecordSessionsPercent(
    process.env.REACT_APP_MIXPANEL_RECORD_SESSIONS_PERCENT,
  )

// The current page may be one nothing is sent from; checked on every call, not
// only at load (the app is a single-page one).
const pageAllowed = () => isRecordingAllowed(window.location.pathname)

// A page that may send events but is never in a replay: the token sign-in.
const pageRecordable = () =>
  pageAllowed() &&
  !window.location.pathname.toUpperCase().startsWith(PUBLIC_ROUTE.TOKEN_LOGIN)

/**
 * Identical scheme to the employee app, so one person is one Mixpanel user
 * across both: the bare user id in production, `<environment>:<id>` elsewhere
 * (a dev or feature database reuses small ids that production also has).
 */
export const distinctIdFor = (userId: number | string): string => {
  const env = environment()
  return env === 'production' ? String(userId) : `${env}:${userId}`
}

// Mixpanel stores `null` / `undefined` as values; leave those props out.
const withoutEmpty = (props: EventProps = {}) => {
  const kept: Record<string, Primitive> = {}
  Object.keys(props).forEach((key) => {
    const value = props[key]
    if (value !== null && value !== undefined) kept[key] = value
  })
  return kept
}

const guard = (action: () => void) => {
  try {
    action()
  } catch {
    // Analytics is never allowed to break the page.
  }
}

// Decided once per page load, so a sign-out and sign-in is not re-rolled.
const sampledForRecording = (): boolean => {
  if (sampled === null) sampled = Math.random() * 100 < recordSessionsPercent()
  return sampled
}

// The recorder's own state: it may have resumed this tab's recording without being asked.
const recordingActive = (): boolean => {
  try {
    const props = mixpanel.get_session_recording_properties() as {
      $mp_replay_id?: string
    }
    return Boolean(props && props.$mp_replay_id)
  } catch {
    return false
  }
}

// The recorder's start and stop return promises; a rejection must stay silent.
const settled = (result: unknown): Promise<void> =>
  result instanceof Promise
    ? result.then(
        () => undefined,
        () => undefined,
      )
    : Promise.resolve()

// Resolves once `done()` holds, or after `ms`.
const waitUntil = (done: () => boolean, ms: number): Promise<void> =>
  new Promise((resolve) => {
    const deadline = Date.now() + ms
    const poll = () => {
      if (done() || Date.now() >= deadline) resolve()
      else window.setTimeout(poll, POLL_MS)
    }
    poll()
  })

// A real admin is identified on a page that may be recorded, and this page
// load is sampled.
const recordingWanted = (): boolean =>
  identifiedAs !== null &&
  !suspended &&
  pageRecordable() &&
  sampledForRecording()

// A recorder start may land within SETTLE_MS from now.
const expectStart = () => {
  startMayLandUntil = Math.max(startMayLandUntil, Date.now() + SETTLE_MS)
}

// Bring the recorder to the wanted state and wait until it is there.
const reconcile = async (): Promise<void> => {
  for (let round = 0; round < MAX_ROUNDS; round += 1) {
    const wanted = recordingWanted()
    if (wanted) {
      if (!recordingActive() && failedStarts < MAX_FAILED_STARTS) {
        expectStart()
        await settled(mixpanel.start_session_recording())
        // The SDK resolves before its recorder has actually started.
        await waitUntil(recordingActive, SETTLE_MS)
        if (!recordingActive()) failedStarts += 1
      }
    } else {
      // Stop what records now, then every start that lands until the last one
      // asked for (the module's, or the SDK's resume) has had time to, unless
      // recording is wanted again meanwhile: a stop does not cancel a start
      // that has not landed yet.
      while (!recordingWanted()) {
        if (recordingActive()) await settled(mixpanel.stop_session_recording())
        const left = startMayLandUntil - Date.now()
        if (left <= 0) break
        await waitUntil(recordingWanted, Math.min(POLL_MS, left))
      }
    }
    if (recordingWanted() === wanted) return
  }
}

/**
 * ENGG-10416 — record while, and only while, a real admin is identified on a
 * page that may be recorded. Transitions run one at a time, each from the
 * state current when it begins; a recording the SDK resumed by itself is
 * stopped the moment it is seen.
 */
const syncRecording = () => {
  if (!initialised || reconcileQueued) return
  reconcileQueued = true
  transitions = transitions
    .then(() => {
      reconcileQueued = false
      return reconcile()
    })
    // A recorder that will not start or stop must not break the page.
    .catch(() => undefined)
}

// After init or reset the SDK may resume this tab's recording by itself.
const expectSdkResume = () => {
  expectStart()
  syncRecording()
  // Its resume may land after that reconcile stopped watching.
  LATE_CHECKS_MS.forEach((ms) => window.setTimeout(syncRecording, ms))
}

// Remembers the token; the SDK itself is initialised by startSdk.
export const initAnalytics = (): void => {
  const configured = process.env.REACT_APP_MIXPANEL_TOKEN
  if (token || !configured || !pageAllowed()) return
  token = configured
}

// ENGG-10416 — init resumes this tab's previous recording (whatever
// record_sessions_percent says), so the SDK starts only for a real admin on a
// page that may be recorded: never for an impersonated session, never on the
// token sign-in route.
const startSdk = (): boolean => {
  if (initialised) return true
  if (!token || !pageRecordable()) return false
  const configured = token
  guard(() => {
    mixpanel.init(configured, {
      api_host: process.env.REACT_APP_MIXPANEL_API_HOST || DEFAULT_API_HOST,
      autocapture: false,
      track_pageview: false,
      // ENGG-10416 — the SDK never samples a new recording by itself (that
      // would be the sign-in page, before we know whether the session is an
      // impersonation); syncRecording starts and stops it. Text stays
      // readable, inputs are masked, the console is not recorded; images and
      // videos keep the SDK's default (blocked).
      record_sessions_percent: 0,
      record_mask_all_text: false,
      record_mask_all_inputs: true,
      record_console: false,
      // Not the cross-subdomain cookie: the employee app and the admin share
      // the parent domain, and would overwrite each other's super properties.
      persistence: 'localStorage',
      property_blacklist: URL_PROPERTIES,
      save_referrer: false,
    })
    initialised = true
    expectSdkResume()
  })
  // An SDK that failed to initialise stays off for this page load.
  if (!initialised) token = null
  return initialised
}

// Nothing more is sent until a user is identified again.
const stop = () => {
  suspended = true
  identifiedAs = null
  identifiedSignature = ''
  waiting = []
  deferredUser = null
  syncRecording()
}

const send = (event: string, props?: EventProps) =>
  guard(() => mixpanel.track(event, withoutEmpty(props)))

export const identifyUser = (user: AnalyticsUser): void => {
  if (!token || !pageAllowed()) return
  if (user.impersonating) {
    if (!suspended) {
      stop()
      if (initialised) {
        // The identity and super properties the SDK persisted for this
        // browser (a regular session of the same admin, before the
        // impersonation) go too. Its reset stops the recording, then may
        // resume one by itself.
        guard(() => mixpanel.reset())
        expectSdkResume()
      }
    }
    return
  }
  // Not on the token sign-in route: held events wait, and the admin is
  // identified from the next call on another page, or by the retry.
  if (!startSdk()) {
    deferredUser = user
    suspended = false
    if (deferredRetry === null) {
      deferredRetry = window.setTimeout(() => {
        deferredRetry = null
        if (deferredUser && !identifiedAs) identifyUser(deferredUser)
      }, DEFERRED_RETRY_MS)
    }
    return
  }
  deferredUser = null

  const distinctId = distinctIdFor(user.id)
  const fields: EventProps = {
    // A string, like the employee app's: one property type across both apps.
    client_id:
      user.clientId === null || user.clientId === undefined
        ? undefined
        : String(user.clientId),
    client_name: user.clientName,
    role: user.role,
    is_tangelo_staff: Boolean(user.isTangeloStaff),
  }
  const profile = withoutEmpty(fields)
  // Fields this user does not have: whatever an earlier identity left behind
  // must not keep being sent, so they are removed, not just left out.
  const missing = PROFILE_KEYS.filter((key) => !(key in profile))
  const signature = JSON.stringify([distinctId, profile])
  if (signature === identifiedSignature) {
    // The same admin again (the identity effect re-runs): a recording stopped
    // on a page that is not recorded resumes here.
    syncRecording()
    return
  }

  guard(() => {
    // Another person on this browser starts a fresh Mixpanel identity, so the
    // two are never merged. Decided from the identity the SDK persisted, not
    // from this page load's memory: after a reload the memory is empty, but the
    // previous person's id is still in storage.
    const persistedUser = mixpanel.get_property('$user_id')
    const persistedOther =
      persistedUser !== undefined &&
      persistedUser !== null &&
      String(persistedUser) !== distinctId
    if (persistedOther || (identifiedAs && identifiedAs !== distinctId)) {
      // The SDK's reset stops any recording; syncRecording restarts it below.
      mixpanel.reset()
      expectSdkResume()
    }
    mixpanel.identify(distinctId)
    mixpanel.register({ app: 'admin', environment: environment(), ...profile })
    mixpanel.people.set(profile)
    missing.forEach((key) => mixpanel.unregister(key))
    if (missing.length > 0) mixpanel.people.unset([...missing])
    identifiedAs = distinctId
    identifiedSignature = signature
    suspended = false
  })
  syncRecording()

  if (identifiedAs !== distinctId) return
  const toSend = waiting
  waiting = []
  toSend.forEach(({ event, props }) => send(event, props))
}

export const resetAnalytics = (): void => {
  stop()
  if (!initialised) return
  guard(() => {
    // The SDK's reset stops the recording, then may resume one by itself.
    mixpanel.reset()
    expectSdkResume()
  })
}

export const track = (event: string, props?: EventProps): void => {
  if (!token) return
  if (suspended) {
    // Nothing is sent, and nothing may be recording either.
    syncRecording()
    return
  }
  if (deferredUser && !identifiedAs) identifyUser(deferredUser)
  if (!pageAllowed()) {
    // A page nothing may be sent from is not recorded either.
    syncRecording()
    return
  }
  // The page it happened on (pathname only), taken now: a held event keeps it.
  const withPage = { ...props, current_path: window.location.pathname }
  if (identifiedAs) {
    // Back from a page that is not recorded, the recording resumes here.
    syncRecording()
    send(event, withPage)
    return
  }
  // Nobody identified yet: nothing may be recording either.
  syncRecording()
  // Held with the time it happened, not the time it is sent (in seconds, as
  // Mixpanel's `time` property is).
  if (waiting.length < MAX_WAITING_EVENTS) {
    waiting.push({ event, props: { ...withPage, time: Date.now() / 1000 } })
  }
}
